/**
 * cmsMediaUrl — single point of media URL resolution across all three
 * image-paths (cms_products.image_media_id / cms_product_pages.hero_media_id
 * / cms_product_pages.blocks.modules[*].images[*].media_uuid).
 *
 * `uuid` is the cms_media.uuid. `variant` chooses among thumb/small/medium/
 * large/xlarge/original (default medium). Returns undefined if the uuid isn't
 * in the generated `cmsMediaIndex` — callers must handle that (currently each
 * public-page branch renders a fallback / blank box).
 *
 * RESPONSIVE LADDER (R4, 2026-10-30)
 * ----------------------------------
 * The media library now writes a ladder of WebP rungs beside each original
 * (thumb 320 / small 640 / medium 1024 / large 1600 / xlarge 2048, only the
 * rungs strictly narrower than the source). The generated index carries TWO
 * facts per rung:
 *
 *   urls[key]    the file's public path
 *   widths[key]  the file's TRUE pixel width
 *
 * The width is the part that matters. A `srcset` candidate is only honest if
 * the browser is told how wide the file really is; a wrong `w` descriptor
 * makes the browser pick a file that is too small and then UPSCALE it, which
 * looks worse than the single 1024px file it replaced. So a rung is a
 * candidate only when it has BOTH a url and a known width — and the width
 * always comes from the file, never from the nominal rung size (a 763px
 * source has a "1024" rung that is really 763px wide).
 *
 * The original is NEVER a `srcset` candidate and never the `src` of the
 * shared <CmsImage> component: it can be several megabytes, and a phone must
 * never be told to download one.
 */
import { cmsMediaIndex } from '@/content/cms/media.generated'

/** The responsive ladder's rungs, narrowest first. */
export type CmsMediaVariant = 'thumb' | 'small' | 'medium' | 'large' | 'xlarge'

/**
 * The rungs in ascending width order.
 *
 * The nominal widths (320 / 640 / 1024 / 1600 / 2048) live on the server
 * (server/lib/media-variants.ts) and are deliberately NOT duplicated here:
 * the client needs the ORDER (to pick the largest rung and to sort the
 * srcset), and the true width of each rung comes from the index.
 */
export const CMS_MEDIA_VARIANTS: readonly CmsMediaVariant[] = [
  'thumb',
  'small',
  'medium',
  'large',
  'xlarge',
]

export function cmsMediaUrl(
  uuid: string | null | undefined,
  variant: CmsMediaVariant | 'original' = 'medium',
): string | undefined {
  if (!uuid) return undefined
  const entry = cmsMediaIndex[uuid]
  if (!entry) return undefined
  return entry.urls[variant] ?? entry.urls.original
}

/**
 * The `srcset` for a media row, or undefined when there is nothing usable.
 *
 * One candidate per rung that has BOTH a url and a known true width, in
 * ascending width order:
 *
 *   "/uploads/2026-10/x.thumb.webp 320w, /uploads/2026-10/x.small.webp 640w"
 *
 * A rung with no width is skipped rather than guessed — see the header.
 */
export function cmsMediaSrcSet(uuid: string | null | undefined): string | undefined {
  if (!uuid) return undefined
  const entry = cmsMediaIndex[uuid]
  if (!entry) return undefined
  const widths = entry.widths ?? {}
  const candidates: string[] = []
  for (const variant of CMS_MEDIA_VARIANTS) {
    const url = entry.urls[variant]
    const width = widths[variant]
    if (!url) continue
    if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) continue
    candidates.push(`${url} ${width}w`)
  }
  return candidates.length > 0 ? candidates.join(', ') : undefined
}

/**
 * The LARGEST WebP rung available for a media row, or undefined.
 *
 * This is the `src` of the shared <CmsImage> component: the fallback for a
 * browser that ignores `srcset`, and the file a screen reader / crawler
 * reads. It is deliberately never the original — an original can be several
 * megabytes, and no layout on this site needs one.
 *
 * Returns undefined when the row has no WebP rung at all (a PDF, a video, or
 * a row whose variants were never generated). Callers must handle that: the
 * shared component renders no <img> at all rather than an <img> pointing at
 * something that is not an image.
 */
export function cmsMediaLargestWebpUrl(uuid: string | null | undefined): string | undefined {
  if (!uuid) return undefined
  const entry = cmsMediaIndex[uuid]
  if (!entry) return undefined
  for (let i = CMS_MEDIA_VARIANTS.length - 1; i >= 0; i -= 1) {
    const url = entry.urls[CMS_MEDIA_VARIANTS[i]!]
    if (url) return url
  }
  return undefined
}
